// DEMO (impl-demo-mock) — remove with services/demo.
//
// Client-side consumption of the server's demo hint. The scenario DATA lives
// server-side; the client only needs CONC defaults + which compounds must stay
// addable in the Add-Ingredient list even when already in the flask.

import type { FormulationEntry } from "@/utils/types/compound";

// Conc-unit → ppm-equivalent factors. A row stored as 640 ppb must read as
// 0.64 ppm (not 640) when compared against a CONC default in ppm.
const CONC_TO_PPM: Record<string, number> = { ppm: 1, ppb: 1e-3, ppt: 1e-6 };

export type DemoClientHint = {
  scenarioKey: string;
  /** "compound:<id>" -> default ppm applied when added/clicked. */
  concDefaults: Record<string, number>;
  /** "compound:<id>" list that must stay addable even when in the flask. */
  forceAddable: string[];
};

/** Default ppm for a compound when added from the list, or undefined. */
export function demoConcDefault(
  hint: DemoClientHint | null | undefined,
  compoundId: string,
): number | undefined {
  return hint?.concDefaults[compoundId];
}

/** Set of "compound:<id>" that should remain clickable in Add-Ingredient. */
export function demoForceAddableSet(
  hint: DemoClientHint | null | undefined,
): ReadonlySet<string> {
  return new Set(hint?.forceAddable ?? []);
}

/**
 * Force-addable compounds that have been ADDED at (≥) their conc default and so
 * should be REMOVED from the Add-Ingredient list entirely on the next fetch —
 * e.g. δ-decalactone, already in the AF flask at 0.64 ppm, stays a clickable
 * "+" until the user bumps it to its 50 ppm default, after which it disappears
 * from the list (rather than lingering as a disabled row). Compounds without a
 * conc default (e.g. 4-ethylbenzaldehyde) are never hidden — there is no
 * "added" signal to distinguish them. Amounts are normalized to ppm-equiv first
 * (a row stored as 640 ppb must read as 0.64 ppm, not 640).
 */
export function demoHiddenIngredients(
  hint: DemoClientHint | null | undefined,
  formulation: ReadonlyArray<FormulationEntry>,
): ReadonlySet<string> {
  const out = new Set<string>();
  if (!hint) return out;
  const forceAddable = demoForceAddableSet(hint);
  for (const f of formulation) {
    if (!forceAddable.has(f.compoundId)) continue;
    const def = hint.concDefaults[f.compoundId];
    if (def === undefined) continue;
    const factor = f.amountUnit ? (CONC_TO_PPM[f.amountUnit] ?? 1) : 1;
    if (f.ppm * factor >= def) out.add(f.compoundId);
  }
  return out;
}
